import type { Track, TrackState } from '../../api.js';
import type { BuildStatus } from './types.js';

const DONE_STATES: TrackState[] = ['DONE', 'MERGED'];
const FAILED_STATES: TrackState[] = ['FAILED_BUILD', 'FAILED_SCOPE'];
const ATTENTION_STATES: TrackState[] = ['BLOCKED_HUMAN', 'AWAITING_PREREQS'];

export interface StatusVisual {
  status: BuildStatus;
  label: string;
}

export function statusForTrackState(state: TrackState): StatusVisual {
  if (state === 'BUILDING') return { status: 'running', label: 'Running' };
  if (state === 'QUEUED') return { status: 'queued', label: 'Queued' };
  if (DONE_STATES.includes(state)) return { status: 'passed', label: 'Passed' };
  if (FAILED_STATES.includes(state)) return { status: 'failed', label: 'Failed' };
  if (ATTENTION_STATES.includes(state)) return { status: 'failed', label: 'Attention' };
  return { status: 'queued', label: state };
}

export type StageState = 'done' | 'active' | 'attention' | 'failed';

export interface StageInfo {
  stage: number;
  stageState: StageState;
}

const ACTIONABLE_STATES: TrackState[] = ['SPEC_REVIEW', 'PR_OPEN', 'AWAITING_PREREQS', 'BLOCKED_HUMAN'];

export function isActionable(state: TrackState): boolean {
  return ACTIONABLE_STATES.includes(state);
}

type Verdict = 'approve' | 'flag' | 'block' | null | undefined;

interface TrackReviewFields {
  review_verdict?: Verdict;
  ciso_verdict?: Verdict;
}

export interface ReviewFlag {
  key: 'cto' | 'ciso';
  label: string;
  verdict: 'flag' | 'block';
}

export function reviewFlags(track: Track): ReviewFlag[] {
  const { review_verdict, ciso_verdict } = track as Track & TrackReviewFields;
  const flags: ReviewFlag[] = [];
  if (review_verdict === 'flag') flags.push({ key: 'cto', label: 'CTO ⚑', verdict: 'flag' });
  if (review_verdict === 'block') flags.push({ key: 'cto', label: 'CTO ✕', verdict: 'block' });
  if (ciso_verdict === 'flag') flags.push({ key: 'ciso', label: 'CISO ⚑', verdict: 'flag' });
  if (ciso_verdict === 'block') flags.push({ key: 'ciso', label: 'CISO ✕', verdict: 'block' });
  return flags;
}

export type BoardColumn = 'spec_review' | 'queued' | 'building' | 'in_review' | 'done';

const BOARD_SPEC_REVIEW_STATES: TrackState[] = ['DRAFTING_SPEC', 'SPEC_REVIEW'];
const BOARD_QUEUED_STATES: TrackState[] = ['QUEUED'];
const BOARD_BUILDING_STATES: TrackState[] = ['BUILDING'];
const BOARD_IN_REVIEW_STATES: TrackState[] = ['VERIFYING', 'PR_OPEN'];
const BOARD_DONE_STATES: TrackState[] = ['DONE', 'MERGED'];

export function boardColumnFor(track: Track): BoardColumn | null {
  const { state } = track;
  if (BOARD_SPEC_REVIEW_STATES.includes(state)) return 'spec_review';
  if (BOARD_QUEUED_STATES.includes(state)) return 'queued';
  if (BOARD_BUILDING_STATES.includes(state)) return 'building';
  if (BOARD_IN_REVIEW_STATES.includes(state)) return 'in_review';
  if (BOARD_DONE_STATES.includes(state)) return 'done';
  return null;
}

export type ParkedLane = 'blocked' | 'cancelled';

const PARKED_BLOCKED_STATES: TrackState[] = ['BLOCKED_HUMAN', 'AWAITING_PREREQS'];
const PARKED_CANCELLED_STATES: TrackState[] = ['CANCELLED', 'FAILED_BUILD', 'FAILED_SCOPE'];

export function parkedLaneFor(track: Track): ParkedLane | null {
  if (PARKED_BLOCKED_STATES.includes(track.state)) return 'blocked';
  if (PARKED_CANCELLED_STATES.includes(track.state)) return 'cancelled';
  return null;
}

export interface BoardReviewChip {
  key: 'cto' | 'ciso';
  label: string;
  verdict: 'ok' | 'flag' | 'block';
}

// Board-only variant of reviewFlags() that also surfaces 'approve' verdicts as
// subtle ok chips. Kept separate from reviewFlags() so the existing Dashboard
// (BuildRow/ActionStrip) rendering is unaffected.
export interface NeedsInputInfo {
  active: boolean;
  question: string | null;
  since: string | null;
}

interface TrackForemanFields {
  foreman_state?: Record<string, boolean> | null;
  needs_input_at?: string | null;
  last_session_summary?: string | null;
}

// Operator-facing summary text is rendered as plain React text (JSX text
// nodes / the `title` attribute), which React already escapes on output —
// re-escaping it here would double-encode and corrupt legitimate content
// (e.g. "&amp;" showing up literally). Bounding the length is still worth
// doing so an oversized summary can't blow up a native tooltip or the drawer.
const UI_SUMMARY_MAX_LENGTH = 500;

function boundSummary(text: string): string {
  return text.length > UI_SUMMARY_MAX_LENGTH
    ? `${text.slice(0, UI_SUMMARY_MAX_LENGTH)}…`
    : text;
}

// Paused-session flag set by POST /api/session-log (outcome: needs_input).
// Not a track state — read straight off foreman_state so the badge tracks
// the flag regardless of which board/drawer surface renders it.
export function needsInputInfo(track: Track): NeedsInputInfo {
  const { foreman_state, needs_input_at, last_session_summary } = track as Track & TrackForemanFields;
  const active = Boolean(foreman_state?.needs_input);
  return {
    active,
    question: active && last_session_summary ? boundSummary(last_session_summary) : null,
    since: active ? needs_input_at ?? null : null,
  };
}

export type AttentionKind = 'halted' | 'needs_input';

export interface AttentionState {
  kind: AttentionKind;
  reason: string | null;
  since: string | null;
  sessionUrl: string | null;
}

// Same terminal-state predicate the server's stale-flag cleanup (foreman.ts
// clearStaleForemanState) uses to decide which tracks' needs_input/foreman_state
// flags are stale. Mirrored here so a track that finished (merged/DONE/CANCELLED)
// stops surfacing "needs you" immediately, without waiting on the next cleanup
// tick to null out the underlying flags server-side.
function isTerminal(track: Track): boolean {
  return track.merged_at !== null || track.state === 'DONE' || track.state === 'CANCELLED';
}

// Combines the two "a Claude Code session stopped without finishing" signals
// into one badge-ready shape: needs_input (foreman_state flag, paused mid-build
// waiting on an answer) and halted (last_session_outcome recorded by
// POST /api/session-log, session ended without completing). needs_input takes
// priority when both are somehow set, since it's the more actionable of the two.
// A terminal track never needs attention, even if a stale flag lingers.
export function attentionStateFor(track: Track): AttentionState | null {
  if (isTerminal(track)) return null;
  const needsInput = needsInputInfo(track);
  const { last_session_outcome, session_url } = track;
  if (needsInput.active) {
    return { kind: 'needs_input', reason: needsInput.question, since: needsInput.since, sessionUrl: session_url };
  }
  if (last_session_outcome === 'halted') {
    const { last_session_summary } = track;
    return {
      kind: 'halted',
      reason: last_session_summary ? boundSummary(last_session_summary) : null,
      since: track.updated_at,
      sessionUrl: session_url,
    };
  }
  return null;
}

export function attentionTracks(tracks: Track[]): Track[] {
  return tracks.filter((t) => attentionStateFor(t) !== null);
}

export function boardReviewFlags(track: Track): BoardReviewChip[] {
  const { review_verdict, ciso_verdict } = track as Track & TrackReviewFields;
  const chips: BoardReviewChip[] = [];
  if (review_verdict === 'approve') chips.push({ key: 'cto', label: 'CTO ✓', verdict: 'ok' });
  if (review_verdict === 'flag') chips.push({ key: 'cto', label: 'CTO ⚑', verdict: 'flag' });
  if (review_verdict === 'block') chips.push({ key: 'cto', label: 'CTO ✕', verdict: 'block' });
  if (ciso_verdict === 'approve') chips.push({ key: 'ciso', label: 'CISO ✓', verdict: 'ok' });
  if (ciso_verdict === 'flag') chips.push({ key: 'ciso', label: 'CISO ⚑', verdict: 'flag' });
  if (ciso_verdict === 'block') chips.push({ key: 'ciso', label: 'CISO ✕', verdict: 'block' });
  return chips;
}

function baseStage(track: Pick<Track, 'approved_at' | 'built_at' | 'merged_at'>): number {
  if (track.merged_at) return 3;
  if (track.built_at) return 2;
  if (track.approved_at) return 1;
  return 0;
}

export function stageFor(track: Track): StageInfo {
  switch (track.state) {
    case 'DRAFTING_SPEC':
      return { stage: 0, stageState: 'active' };
    case 'SPEC_REVIEW':
      return { stage: 0, stageState: 'attention' };
    case 'AWAITING_PREREQS':
      return { stage: 0, stageState: 'attention' };
    case 'QUEUED':
      return { stage: 0, stageState: 'done' };
    case 'BUILDING':
      return { stage: 1, stageState: 'active' };
    case 'VERIFYING':
      return { stage: 2, stageState: 'active' };
    case 'PR_OPEN':
      return { stage: 2, stageState: 'attention' };
    case 'BLOCKED_HUMAN':
      return { stage: baseStage(track), stageState: 'attention' };
    case 'MERGED':
      return track.deploy_status === 'SUCCESS'
        ? { stage: 4, stageState: 'done' }
        : { stage: 4, stageState: 'active' };
    case 'DONE':
      return { stage: 4, stageState: 'done' };
    case 'FAILED_BUILD':
    case 'FAILED_SCOPE':
      return { stage: baseStage(track), stageState: 'failed' };
    case 'CANCELLED':
      return { stage: baseStage(track), stageState: 'failed' };
    default: {
      const exhaustiveCheck: never = track.state;
      return exhaustiveCheck;
    }
  }
}
